import { ButtonBase, SvgIconTypeMap, Typography } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { FunctionComponent, SVGProps } from 'react';
import { makeStyles } from 'tss-react/mui';
import { getResponsiveFontSize } from 'shared/utils';

type MapControlButtonSize = 'large' | 'small';

type MapControlButtonProps =
  | {
      Icon?:
        | FunctionComponent<SVGProps<SVGSVGElement>>
        | OverridableComponent<SvgIconTypeMap>;
      text: string;
      onClick: () => void;
      disabled?: boolean;
      className?: string;
      size?: MapControlButtonSize;
    }
  | {
      Icon:
        | FunctionComponent<SVGProps<SVGSVGElement>>
        | OverridableComponent<SvgIconTypeMap>;
      ariaLabel: string;
      onClick: () => void;
      disabled?: boolean;
      className?: string;
      size?: MapControlButtonSize;
    };

const useButtonStyles = makeStyles<{ size: MapControlButtonSize }>()((
  theme,
  { size },
) => {
  const { spacing, palette } = theme;

  const buttonWidthHeightSpacing = size === 'large' ? 7 : 4;
  const iconWidthHeightPx = size === 'large' ? 24 : 16;

  return {
    controlButton: {
      width: `${spacing(buttonWidthHeightSpacing)} !important`,
      height: `${spacing(buttonWidthHeightSpacing)} !important`,
      display: 'flex !important',
      flexDirection: 'column',
      backgroundColor: palette.common.white,
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
      borderRadius: '8px',
      margin: '16px 16px 0 16px',
      pointerEvents: 'auto',
      borderColor: theme.palette.grey[300],
    },
    controlIcon: {
      width: iconWidthHeightPx,
      height: iconWidthHeightPx,
      color: palette.text.primary,
    },
    textNoIcon: {
      fontSize: getResponsiveFontSize('0.875rem'), // 14px
      fontVariant: 'all-small-caps',
      fontWeight: theme.typography.fontWeightBold,
      padding: 0,
      margin: 0,
    },
  };
});

export const useButtonGroupStyles = makeStyles()((theme) => {
  return {
    groupTopButton: {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      borderBottomWidth: 1,
      borderBottomStyle: 'solid',
      borderBottomColor: theme.palette.grey[300],
    },
    groupBottomButton: {
      marginTop: 0,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
  };
});

export const MapControlButton = (props: MapControlButtonProps): JSX.Element => {
  const { size = 'large', Icon, onClick, disabled, className = '' } = props;
  const { classes } = useButtonStyles({ size });

  const fullClassName = `${classes.controlButton} ${className}`;
  if ('text' in props) {
    const { text } = props;

    return (
      <ButtonBase
        className={fullClassName}
        onClick={onClick}
        disabled={disabled}
      >
        {Icon && <Icon className={classes.controlIcon} />}
        <Typography
          className={Icon ? '' : classes.textNoIcon}
          variant="all-small-caps"
        >
          {text}
        </Typography>
      </ButtonBase>
    );
  }

  const { ariaLabel } = props;

  return (
    <ButtonBase
      className={fullClassName}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {Icon && <Icon className={classes.controlIcon} />}
    </ButtonBase>
  );
};
